import { useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SeverityBadge } from "@/components/SeverityBadge";
import { Crosshair, Play, Terminal, Cube, StopCircle, Bug } from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPE_LABELS = {
  reconnaissance: "Reconnaissance", vulnerability_scan: "Vulnerability Scan",
  exploitation: "Exploitation", web_application: "Web Application",
  network_scan: "Network Scan", social_engineering: "Social Engineering",
  privilege_escalation: "Privilege Escalation", full_pentest: "Full Pentest",
};
const SEV_KEYS = ["critical", "high", "medium", "low", "info"];
const sevColor = { critical: "#FF3B30", high: "#FF9500", medium: "#FFCC00", low: "#00F0FF", info: "#71717A" };

export default function Pentest() {
  const [meta, setMeta] = useState(null);
  const [target, setTarget] = useState("");
  const [type, setType] = useState("full_pentest");
  const [provider, setProvider] = useState("openai");
  const [model, setModel] = useState("gpt-5.2");
  const [tasks, setTasks] = useState([]);
  const [active, setActive] = useState(null);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const loadAll = useCallback(async () => {
    const [m, t, s] = await Promise.all([
      api.get("/pentest/tools"), api.get("/pentest/tasks"), api.get("/pentest/stats"),
    ]);
    setMeta(m.data); setTasks(t.data.tasks); setStats(s.data);
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  // Poll the active task while it is running.
  useEffect(() => {
    if (active && active.status === "running") {
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/pentest/tasks/${active.id}`);
          setActive(data);
          setTasks((p) => p.map((x) => (x.id === data.id ? data : x)));
          if (data.status !== "running") {
            clearInterval(pollRef.current);
            api.get("/pentest/stats").then((r) => setStats(r.data));
          }
        } catch (e) {}
      }, 2500);
      return () => clearInterval(pollRef.current);
    }
  }, [active?.id, active?.status]);

  const launch = async (e) => {
    e.preventDefault();
    if (!target.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/pentest/tasks", { target: target.trim(), type, llm_provider: provider, llm_model: model });
      setTasks((p) => [data, ...p]);
      setActive(data);
      toast.success(`PentAGI agent launched on ${data.target}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Launch failed");
    } finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!active) return;
    const { data } = await api.post(`/pentest/tasks/${active.id}/cancel`);
    setActive(data);
    setTasks((p) => p.map((x) => (x.id === data.id ? data : x)));
  };

  const providerModels = meta?.llm_providers.find((p) => p.name.toLowerCase().includes(provider))?.models
    || meta?.llm_providers[0]?.models || [];

  return (
    <div data-testid="pentest-page">
      <div className="mb-6">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#00F0FF]">PentAGI · Autonomous AI Agent</p>
        <h1 className="font-head text-3xl tracking-tighter font-black mt-1">Run a Pentest</h1>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[["Total Tasks", stats.total, "#00F0FF"], ["Running", stats.running, "#FFCC00"], ["Completed", stats.completed, "#3ECF8E"], ["Critical Findings", stats.findings_by_severity.critical, "#FF3B30"]].map(([l, v, c]) => (
            <div key={l} className="bg-[#0C0C14] border border-[#1F1F33] p-4">
              <div className="text-[10px] uppercase tracking-[0.15em] text-[#71717A]">{l}</div>
              <div className="font-head text-3xl font-black mt-1" style={{ color: c }}>{v}</div>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-6">
          <form onSubmit={launch} className="bg-[#0C0C14] border border-[#1F1F33] p-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Target</label>
                <input data-testid="pentest-target-input" value={target} onChange={(e) => setTarget(e.target.value)}
                  placeholder="acme-corp.com / 10.0.4.12"
                  className="w-full bg-[#05050A] border border-[#1F1F33] px-3 py-2.5 text-sm outline-none focus:border-[#00F0FF] placeholder:text-[#71717A]" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Test Type</label>
                <select data-testid="pentest-type-select" value={type} onChange={(e) => setType(e.target.value)}
                  className="w-full bg-[#05050A] border border-[#1F1F33] px-3 py-2.5 text-sm outline-none focus:border-[#00F0FF]">
                  {(meta?.test_types || []).map((t) => <option key={t} value={t}>{TYPE_LABELS[t] || t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">LLM Provider</label>
                <select data-testid="pentest-provider-select" value={provider}
                  onChange={(e) => { const p = e.target.value; setProvider(p); const pm = meta?.llm_providers.find((x) => x.name.toLowerCase().includes(p)); setModel(pm?.models[0] || ""); }}
                  className="w-full bg-[#05050A] border border-[#1F1F33] px-3 py-2.5 text-sm outline-none focus:border-[#00F0FF]">
                  {(meta?.llm_providers || []).map((p) => <option key={p.name} value={p.name.toLowerCase().split(" ")[0]}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Model</label>
                <select data-testid="pentest-model-select" value={model} onChange={(e) => setModel(e.target.value)}
                  className="w-full bg-[#05050A] border border-[#1F1F33] px-3 py-2.5 text-sm outline-none focus:border-[#00F0FF]">
                  {providerModels.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>
            <button data-testid="run-pentest-button" type="submit" disabled={busy}
              className="flex items-center gap-2 bg-[#00F0FF] text-black font-bold uppercase tracking-[0.15em] text-[11px] px-5 py-2.5 hover:bg-white active:scale-[0.98] transition-all disabled:opacity-50">
              <Play size={14} weight="fill" /> {busy ? "Launching…" : "Launch Agent"}
            </button>
          </form>

          {active && (
            <div className="bg-[#0C0C14] border border-[#1F1F33] p-5 fade-up" data-testid="pentest-active">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <Crosshair size={16} weight="duotone" color="#00F0FF" className={active.status === "running" ? "pulse-dot" : ""} />
                    <span className="font-head text-lg font-bold">{active.target}</span>
                  </div>
                  <div className="text-[11px] text-[#71717A] mt-1">{TYPE_LABELS[active.type]} · {active.agent_config?.model}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 border"
                    style={{ color: active.status === "completed" ? "#3ECF8E" : active.status === "running" ? "#FFCC00" : "#FF3B30", borderColor: "#1F1F33" }}
                    data-testid="pentest-status">{active.status}</span>
                  {active.status === "running" && (
                    <button data-testid="cancel-pentest-button" onClick={cancel} className="flex items-center gap-1 text-[11px] uppercase text-[#FF3B30] border border-[#FF3B30]/40 px-2 py-1 hover:bg-[#FF3B30]/10">
                      <StopCircle size={13} /> Cancel
                    </button>
                  )}
                </div>
              </div>

              <div className="w-full h-1.5 bg-[#1F1F33] mb-1">
                <div className="h-full bg-[#00F0FF] transition-all duration-500" style={{ width: `${active.progress}%` }} data-testid="pentest-progress" />
              </div>
              <div className="text-right text-[10px] text-[#71717A] mb-4">{active.progress}%</div>

              {active.commands_executed?.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2"><Terminal size={13} color="#00F0FF" /><span className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Agent Terminal</span></div>
                  <div className="bg-black border border-[#1F1F33] p-3 max-h-52 overflow-y-auto text-[11px] font-mono">
                    {active.commands_executed.map((c) => (
                      <div key={c.step} className="mb-2">
                        <div className="text-[#3ECF8E]">$ {c.command}</div>
                        <div className="text-[#71717A] whitespace-pre-wrap">{c.output}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {active.findings?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2"><Bug size={13} color="#FF3B30" /><span className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Findings ({active.findings.length})</span></div>
                  <div className="divide-y divide-[#1F1F33] border border-[#1F1F33]">
                    {active.findings.map((f) => (
                      <div key={f.id} className="px-4 py-3" data-testid={`finding-${f.id}`}>
                        <div className="flex items-center gap-3 flex-wrap">
                          <SeverityBadge level={f.severity === "info" ? "low" : f.severity} />
                          <span className="text-sm">{f.title}</span>
                          {f.cve && <span className="text-[10px] font-mono text-[#FF9500] border border-[#FF9500]/40 px-1.5">{f.cve}</span>}
                          {f.mitre_technique && <span className="text-[10px] font-mono text-[#71717A] border border-[#1F1F33] px-1.5">{f.mitre_technique}</span>}
                        </div>
                        <p className="text-xs text-[#A1A1AA] mt-1">{f.description}</p>
                        {f.recommendation && <p className="text-[11px] text-[#00F0FF] mt-1 border-l-2 border-[#00F0FF] pl-2">{f.recommendation}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Task History</div>
            <div className="grid gap-2" data-testid="pentest-history">
              {tasks.map((t) => (
                <button key={t.id} onClick={() => setActive(t)}
                  className={`flex items-center gap-3 border px-4 py-3 text-left transition-colors ${active?.id === t.id ? "border-[#00F0FF] bg-[#00F0FF]/5" : "border-[#1F1F33] bg-[#0C0C14] hover:border-[#33334D]"}`}>
                  <span className="font-mono text-sm flex-1 truncate">{t.target}</span>
                  <span className="text-[10px] text-[#71717A]">{TYPE_LABELS[t.type]}</span>
                  <span className="text-[10px] text-[#71717A]">{t.findings?.length || 0} findings</span>
                  <span className="text-[10px] uppercase" style={{ color: t.status === "completed" ? "#3ECF8E" : t.status === "running" ? "#FFCC00" : "#FF3B30" }}>{t.status}</span>
                </button>
              ))}
              {tasks.length === 0 && <div className="text-xs text-[#71717A] py-6 text-center border border-dashed border-[#1F1F33]">No pentest tasks yet.</div>}
            </div>
          </div>
        </div>

        {/* Tool catalog */}
        <div className="xl:col-span-1">
          <div className="bg-[#0C0C14] border border-[#1F1F33] p-5 sticky top-24">
            <div className="flex items-center gap-2 mb-3"><Cube size={16} weight="duotone" color="#00F0FF" /><span className="text-[10px] uppercase tracking-[0.25em] text-[#71717A]">Kali Tool Arsenal</span></div>
            <div className="space-y-3 max-h-[560px] overflow-y-auto">
              {meta && Object.entries(meta.tools).map(([cat, tools]) => (
                <div key={cat}>
                  <div className="text-[10px] uppercase tracking-wider text-[#00F0FF] mb-1.5">{cat.replace("_", " ")}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {tools.map((t) => <span key={t} className="text-[10px] font-mono text-[#A1A1AA] border border-[#1F1F33] px-1.5 py-0.5">{t}</span>)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
